import {
	CatalogPriceDriftDirection,
	type GetOrderQuoteOfferBatchesQuery,
	useApplyCatalogPriceToOfferSampleMutation,
} from '@codezo/graphql';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { getMutationError } from '../../../lib/mutation-response';
import { ConfirmDialog } from '../../ui/confirm-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';

type Sample = NonNullable<
	GetOrderQuoteOfferBatchesQuery['getOrderQuoteOfferBatches'][number]['offers'][number]['sample']
>;
type Drift = NonNullable<Sample['catalogPriceDrift']>;

/**
 * The catalogue sample price moved since this sample was priced.
 *
 * The sample counterpart of CatalogDriftBadge, with one action instead of two:
 * a sample has no cost under its price, so there is no "keep the client price"
 * — the client price is the whole figure. Every word is the backend's; null
 * (under 2%, no catalogue price, or a waived sample) renders nothing.
 */
export function SampleDriftBadge({
	drift,
	sampleId,
	onApplied,
}: {
	drift?: Drift | null;
	sampleId: string;
	/** Refetch the offer: the badge disappears once the sample matches. */
	onApplied: () => void;
}) {
	const [open, setOpen] = useState(false);
	const [confirming, setConfirming] = useState(false);
	const [apply, { loading }] = useApplyCatalogPriceToOfferSampleMutation();

	if (!drift) return null;
	const up = drift.direction === CatalogPriceDriftDirection.Up;
	const Arrow = up ? ArrowUpRight : ArrowDownRight;
	const { action } = drift;

	const run = async () => {
		try {
			const { data } = await apply({
				variables: { input: { orderQuoteOfferSampleId: sampleId } },
			});
			const error = getMutationError(data?.applyCatalogPriceToOfferSample);
			if (error) {
				toast.error(error);
				return;
			}
			toast.success(data?.applyCatalogPriceToOfferSample.message || 'Sample updated');
			setOpen(false);
			setConfirming(false);
			onApplied();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update the sample');
		}
	};

	/* Always confirmed: it re-prices the sample. The warning (the client has
	   already seen the offer) is added to the dialog when there is one. */
	const choose = () => {
		setOpen(false);
		setConfirming(true);
	};

	return (
		<>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger
					className={cn(
						'inline-flex w-fit items-center gap-1 rounded-full border px-2 py-px text-2xs font-medium transition-colors',
						up
							? 'border-status-warn/30 bg-status-warn-soft text-status-warn hover:bg-status-warn/20'
							: 'border-status-info/30 bg-status-info-soft text-status-info hover:bg-status-info/20'
					)}
				>
					<Arrow size={10} />
					{drift.label}
				</PopoverTrigger>
				<PopoverContent align="end" sideOffset={6} className="w-[26rem] gap-0 p-0">
					<div className="flex flex-col gap-2 px-4 py-3.5">
						<span
							className={cn(
								'inline-flex items-center gap-1.5 text-sm font-medium',
								up ? 'text-status-warn' : 'text-status-info'
							)}
						>
							<Arrow size={12} />
							{drift.label}
						</span>
						<p className="text-sm leading-relaxed text-text-2">{drift.explanation}</p>
					</div>
					<div className="flex flex-col gap-1 border-t border-line-1 bg-surface-fill px-4 pb-4 pt-3">
						<button
							type="button"
							disabled={!action.available || loading}
							onClick={choose}
							className="h-9 rounded-md bg-accent px-3 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45"
						>
							{action.label}
						</button>
						{!action.available && action.unavailableReason && (
							<span className="text-xs text-text-3">{action.unavailableReason}</span>
						)}
					</div>
				</PopoverContent>
			</Popover>

			<ConfirmDialog
				open={confirming}
				onOpenChange={setConfirming}
				title="Update this sample?"
				description={
					<span className="flex flex-col gap-2">
						{action.warning && <span>{action.warning}</span>}
						<span className="text-text-1">{action.label}</span>
					</span>
				}
				confirmLabel="Update"
				variant="default"
				loading={loading}
				onConfirm={() => void run()}
			/>
		</>
	);
}
